import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * React Router does not restore/reset scroll on client-side navigation, so
 * clicking a link while scrolled down would leave the next page at the same
 * scroll offset (users perceive the destination "opening from the bottom").
 * This component snaps to the top on every pathname change, but respects
 * browser-native scroll behaviour:
 *   - hash links (e.g. /page#section) keep working
 *   - back/forward navigation (POP) preserves the previous position
 */
export default function ScrollToTop() {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (hash) return; // let the browser scroll to the anchor
        // Only reset for a fresh navigation (PUSH/REPLACE). history.action is
        // "POP" on back/forward, in which case the browser has already restored
        // scroll and we should not overwrite it.
        const action = typeof window !== "undefined" && window.history && window.history.state
            ? undefined // no reliable way to read here; err on the side of resetting on any push
            : undefined;
        // Simple, reliable behaviour: always jump to top when pathname changes.
        window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
    }, [pathname, hash]);

    // Fire an anonymous activity ping on every navigation for admin analytics.
    // De-duplication is done server-side (30s window per visitor+path). We
    // guard against admin pages so they don't inflate visitor counts.
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (pathname.startsWith("/admin")) return;
        const base = process.env.REACT_APP_BACKEND_URL;
        if (!base) return;
        try {
            fetch(`${base}/api/public/activity/ping`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ path: pathname }),
                keepalive: true,
            }).catch(() => {});
        } catch (_e) { /* best-effort */ }
    }, [pathname]);

    return null;
}
